import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";

// Fetches GET /api/community/overview and adapts it to the shape the container
// and CommunityCard render (COMY1-353). Same approach as useCommunityDashboard:
// the API returns raw snake_case values and `YYYY-MM` months, and all the
// localizing happens here.
//
// The list holds every community the viewer runs as its community manager
// (`managedByMe`) or as its MCM — multi-communities manager, the "MoM".

// One line colour per community on the income graph, in list order.
const SERIES_COLORS = ["#7367F0", "#28C76F", "#FF9F43", "#00CFE8", "#EA5455", "#9C6ADE", "#F4B400", "#2E86DE"];

// Communities without an uploaded image get a generated letter avatar from the
// backend — fine as a logo, but not as a cover banner.
const isRealImage = (url) => Boolean(url) && !url.includes("ui-avatars.com");

/** `2026-07` → "Jul 26" / "יולי 26". */
const formatMonth = (ym, lang) => {
  const [year, month] = ym.split("-").map(Number);
  return new Intl.DateTimeFormat(lang, { month: "short", year: "2-digit", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  );
};

export const useCommunitiesOverview = () => {
  const { i18n } = useTranslation();
  const [raw, setRaw] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiGetRequest(endpoints.getCommunitiesOverview);
      setRaw(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";

  const data = useMemo(() => {
    if (!raw) return null;
    return {
      currencySymbol: raw.currency_symbol || "₪",
      isMoM: Boolean(raw.is_mom),
      months: (raw.months || []).map((ym) => formatMonth(ym, lang)),
      communities: (raw.communities || []).map((c, i) => ({
        id: c.id,
        name: c.name,
        image: c.image,
        cover: isRealImage(c.image) ? c.image : null,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        managedByMe: c.managed_by_me,
        mcmByMe: c.mcm_by_me,
        manager: c.manager ? { name: c.manager.name, avatar: c.manager.avatar } : null,
        members: { current: c.members },
        suppliers: c.suppliers,
        memberAvatars: c.member_avatars || [],
        supplierAvatars: c.supplier_avatars || [],
        revenueThisMonth: c.revenue_this_month,
        earningsThisMonth: c.earnings_this_month,
        income12mo: c.income_12mo || [],
        // Pending items are only returned for communities the viewer manages
        // directly; elsewhere they are null and read as nothing pending.
        needs: {
          joinRequests: c.needs?.join_requests || 0,
          supplierOffers: c.needs?.supplier_offers || 0,
        },
        // Community Piggybank (TBBR-60) and per-community setup are not backed
        // by data yet — null, and the card hides those blocks.
        monthlyGoal: c.monthly_goal,
        goalCollected: c.goal_collected,
        activeProjects: c.active_projects,
        piggybankAvailable: c.piggybank_available,
        setupPercent: c.setup_percent,
      })),
    };
  }, [raw, lang]);

  return { data, isLoading, error, refetch: fetchOverview };
};
